import type {
  QuestionnaireUploadOutput,
  ReportOutlineApprovalStatusOutput,
  ReportOutlineFinalOutput,
  ReportOutlineUploadOutput,
  SourceFileUploadOutput,
  StandardDraftOutput,
  StandardEditDraftOutput,
  StructureUploadOutput,
  UploadCheckOutput,
} from "@/features/create-standard";

export const TEST_DRAFT_ID = "068c8c50-0d1e-7f2a-8b3c-4d5e6f7a8b9c";

export function buildStandardDraft(
  overrides: Partial<StandardDraftOutput> = {}
): StandardDraftOutput {
  return {
    id: TEST_DRAFT_ID,
    expires_at: "2026-09-24T12:00:00Z",
    ...overrides,
  };
}

export function buildReportOutlineCheck(
  overrides: Partial<UploadCheckOutput> = {}
): UploadCheckOutput {
  return {
    check_type: "numbering_format",
    passed: true,
    explanation: 'Section codes follow the numbering format (e.g. "1", "1.2")',
    ...overrides,
  };
}

export function buildReportOutlineUpload(
  overrides: Partial<ReportOutlineUploadOutput> = {}
): ReportOutlineUploadOutput {
  return {
    id: "068c8c51-457c-7b24-8000-41a17b0a37c8",
    file_name: "gistm-report-outline.xlsx",
    file_size: "128.4 KB",
    uploaded_at: "2026-09-14T16:20:00Z",
    status: "passed",
    checks_passed: 2,
    checks_failed: 0,
    is_latest: true,
    checks: [
      buildReportOutlineCheck(),
      buildReportOutlineCheck({
        check_type: "required_cell",
        explanation: "Required columns are filled in",
      }),
    ],
    ...overrides,
  };
}

export function buildSourceFileUpload(
  overrides: Partial<SourceFileUploadOutput> = {}
): SourceFileUploadOutput {
  return {
    id: "068c8c52-1a2b-7c3d-8e4f-5a6b7c8d9e0f",
    file_name: "GISTM_main_standard_2020_v1.pdf",
    file_size: "23.5 MB",
    uploaded_at: "2026-09-14T16:25:00Z",
    status: "uploaded",
    ...overrides,
  };
}

export const reportOutlineUploadPassedMock = buildReportOutlineUpload();

export const reportOutlineUploadFailedMock = buildReportOutlineUpload({
  id: "068c8c51-457c-7b24-8000-41a17b0a37c9",
  file_name: "gistm-report-outline-draft.xlsx",
  status: "failed",
  checks_passed: 1,
  checks_failed: 2,
  checks: [
    buildReportOutlineCheck(),
    buildReportOutlineCheck({
      check_type: "duplicate_identifier",
      passed: false,
      explanation: "Section codes are unique",
    }),
    buildReportOutlineCheck({
      check_type: "mandatory_section",
      passed: false,
      explanation: 'The mandatory "Assessment" section is present exactly once',
    }),
  ],
});

export const reportOutlineUploadHistoryMock: { items: ReportOutlineUploadOutput[] } = {
  items: [
    buildReportOutlineUpload({ checks: [] }),
    buildReportOutlineUpload({
      id: "068c8c51-457c-7b24-8000-41a17b0a37c9",
      file_name: "gistm-report-outline-draft.xlsx",
      uploaded_at: "2026-09-14T15:10:00Z",
      status: "failed",
      checks_passed: 1,
      checks_failed: 2,
      is_latest: false,
      checks: [],
    }),
    buildReportOutlineUpload({
      id: "068c8c51-457c-7b24-8000-41a17b0a37ca",
      file_name: "gistm-report-outline-v0.xlsx",
      uploaded_at: "2026-09-13T09:00:00Z",
      status: "failed",
      checks_passed: 0,
      checks_failed: 1,
      is_latest: false,
      checks: [],
    }),
  ],
};

export const reportOutlineUploadDetailMock = buildReportOutlineUpload({
  status: "failed",
  checks_passed: 1,
  checks_failed: 1,
  checks: [
    buildReportOutlineCheck(),
    buildReportOutlineCheck({
      check_type: "sequence_gap",
      passed: false,
      explanation: "Section numbering has no gaps",
    }),
  ],
});

export function buildStructureCheck(overrides: Partial<UploadCheckOutput> = {}): UploadCheckOutput {
  return {
    check_type: "id_format",
    passed: true,
    explanation: '"id" values are positive whole numbers',
    ...overrides,
  };
}

export function buildStructureUpload(
  overrides: Partial<StructureUploadOutput> = {}
): StructureUploadOutput {
  return {
    id: "068c8c53-6b7c-7d8e-9f0a-1b2c3d4e5f60",
    file_name: "gistm-structure.xlsx",
    file_size: "12.4 KB",
    uploaded_at: "2026-09-14T16:30:00Z",
    status: "passed",
    checks_passed: 2,
    checks_failed: 0,
    is_latest: true,
    level_count: 4,
    levels: [
      { level_name: "Topic", row_count: 1 },
      { level_name: "Principle", row_count: 12 },
      { level_name: "Requirement", row_count: 45 },
      { level_name: "Conformance Criterion", row_count: 226 },
    ],
    checks: [
      buildStructureCheck(),
      buildStructureCheck({
        check_type: "required_cell",
        explanation: "Required columns are filled in",
      }),
    ],
    ...overrides,
  };
}

export const structureUploadPassedMock = buildStructureUpload();

export const structureUploadFailedMock = buildStructureUpload({
  id: "068c8c53-6b7c-7d8e-9f0a-1b2c3d4e5f61",
  file_name: "gistm-structure-draft.xlsx",
  status: "failed",
  checks_passed: 1,
  checks_failed: 2,
  level_count: 3,
  levels: [
    { level_name: "Topic", row_count: 1 },
    { level_name: "Principle", row_count: 12 },
    { level_name: "Requirement", row_count: 40 },
  ],
  checks: [
    buildStructureCheck(),
    buildStructureCheck({
      check_type: "duplicate_id",
      passed: false,
      explanation: '"id" values are unique',
    }),
    buildStructureCheck({
      check_type: "required_cell",
      passed: false,
      explanation: "Required columns are filled in",
    }),
  ],
});

export const structureUploadHistoryMock: { items: StructureUploadOutput[] } = {
  items: [
    buildStructureUpload({ checks: [] }),
    buildStructureUpload({
      id: "068c8c53-6b7c-7d8e-9f0a-1b2c3d4e5f61",
      file_name: "gistm-structure-draft.xlsx",
      uploaded_at: "2026-09-14T15:40:00Z",
      status: "failed",
      checks_passed: 1,
      checks_failed: 2,
      is_latest: false,
      level_count: 3,
      levels: [
        { level_name: "Topic", row_count: 1 },
        { level_name: "Principle", row_count: 12 },
        { level_name: "Requirement", row_count: 40 },
      ],
      checks: [],
    }),
    buildStructureUpload({
      id: "068c8c53-6b7c-7d8e-9f0a-1b2c3d4e5f62",
      file_name: "gistm-structure-v0.xlsx",
      uploaded_at: "2026-09-13T10:00:00Z",
      status: "failed",
      checks_passed: 0,
      checks_failed: 1,
      is_latest: false,
      level_count: 0,
      levels: [],
      checks: [],
    }),
  ],
};

export const structureUploadDetailMock = buildStructureUpload({
  status: "failed",
  checks_passed: 1,
  checks_failed: 1,
  checks: [
    buildStructureCheck(),
    buildStructureCheck({
      check_type: "sequence_gap",
      passed: false,
      explanation: '"id" numbering has no gaps',
    }),
  ],
});

export function buildQuestionnaireCheck(
  overrides: Partial<UploadCheckOutput> = {}
): UploadCheckOutput {
  return {
    check_type: "avi_code_format",
    passed: true,
    explanation: '"avi_code" values follow the code format',
    ...overrides,
  };
}

export function buildQuestionnaireUpload(
  overrides: Partial<QuestionnaireUploadOutput> = {}
): QuestionnaireUploadOutput {
  return {
    id: "068c8c54-7c8d-7e9f-8a1b-2c3d4e5f6a70",
    file_name: "gistm-questionnaire.xlsx",
    file_size: "84.2 KB",
    uploaded_at: "2026-09-24T12:00:00Z",
    status: "passed",
    checks_passed: 2,
    checks_failed: 0,
    avi_count: 292,
    cqi_count: 0,
    is_latest: true,
    checks: [
      buildQuestionnaireCheck(),
      buildQuestionnaireCheck({
        check_type: "hierarchy_level_count_match",
        explanation: "Codes match the standard's hierarchy levels",
      }),
    ],
    ...overrides,
  };
}

export const questionnaireUploadPassedMock = buildQuestionnaireUpload();

export const questionnaireUploadFailedMock = buildQuestionnaireUpload({
  id: "068c8c54-7c8d-7e9f-8a1b-2c3d4e5f6a71",
  file_name: "gistm-questionnaire-draft.xlsx",
  status: "failed",
  checks_passed: 1,
  checks_failed: 2,
  avi_count: 180,
  checks: [
    buildQuestionnaireCheck(),
    buildQuestionnaireCheck({
      check_type: "duplicate_avi_code",
      passed: false,
      explanation: '"avi_code" values are unique',
    }),
    buildQuestionnaireCheck({
      check_type: "required_column",
      passed: false,
      explanation: "Required header columns are present",
    }),
  ],
});

export const questionnaireUploadHistoryMock: { items: QuestionnaireUploadOutput[] } = {
  items: [
    buildQuestionnaireUpload({ checks: [] }),
    buildQuestionnaireUpload({
      id: "068c8c54-7c8d-7e9f-8a1b-2c3d4e5f6a71",
      file_name: "gistm-questionnaire-draft.xlsx",
      uploaded_at: "2026-09-24T11:20:00Z",
      status: "failed",
      checks_passed: 1,
      checks_failed: 2,
      avi_count: 180,
      is_latest: false,
      checks: [],
    }),
    buildQuestionnaireUpload({
      id: "068c8c54-7c8d-7e9f-8a1b-2c3d4e5f6a72",
      file_name: "gistm-questionnaire-v0.xlsx",
      uploaded_at: "2026-09-23T09:00:00Z",
      status: "failed",
      checks_passed: 0,
      checks_failed: 1,
      avi_count: 2577,
      is_latest: false,
      checks: [],
    }),
  ],
};

export const questionnaireUploadDetailMock = buildQuestionnaireUpload({
  status: "failed",
  checks_passed: 1,
  checks_failed: 1,
  checks: [
    buildQuestionnaireCheck(),
    buildQuestionnaireCheck({
      check_type: "avi_parent_match",
      passed: false,
      explanation: 'Each "avi_code" belongs to its "requirement_code_parent"',
    }),
  ],
});

export const sourceFileUploadMock = buildSourceFileUpload();

export const standardDraftMock = buildStandardDraft();

export const FINAL_OUTLINE_UPLOAD_ID = "068c8c51-457c-7b24-8000-41a17b0a37c8";
export const FINAL_OUTLINE_STRUCTURE_ID = "068c8c53-6b7c-7d8e-9f0a-1b2c3d4e5f60";

export function buildFinalOutline(
  overrides: Partial<ReportOutlineFinalOutput> = {}
): ReportOutlineFinalOutput {
  return {
    report_outline_upload_id: FINAL_OUTLINE_UPLOAD_ID,
    structure_upload_id: FINAL_OUTLINE_STRUCTURE_ID,
    is_approved: false,
    approved_at: null,
    items: [
      {
        number: "1",
        depth: 1,
        name: "Introduction",
        name_es: null,
        name_pt: null,
      },
      {
        number: "1.1",
        depth: 2,
        name: "Geographic Location",
        name_es: null,
        name_pt: null,
      },
      {
        number: "2",
        depth: 1,
        name: "Assessment",
        name_es: null,
        name_pt: null,
      },
      {
        number: "2.1",
        depth: 2,
        name: "Principle 1",
        name_es: null,
        name_pt: null,
      },
      {
        number: "2.1.1",
        depth: 3,
        name: "Requirement 1.1",
        name_es: null,
        name_pt: null,
      },
    ],
    ...overrides,
  };
}

export function buildOutlineApprovalStatus(
  overrides: Partial<ReportOutlineApprovalStatusOutput> = {}
): ReportOutlineApprovalStatusOutput {
  return {
    is_approved: true,
    report_outline_upload_id: FINAL_OUTLINE_UPLOAD_ID,
    structure_upload_id: FINAL_OUTLINE_STRUCTURE_ID,
    approved_at: "2026-10-05T12:00:00Z",
    ...overrides,
  };
}

export const finalOutlineMock = buildFinalOutline();

export const outlineApprovalStatusMock = buildOutlineApprovalStatus();

export const EDIT_STANDARD_ID = "42";

export function buildStandardEditDraft(
  overrides: Partial<StandardEditDraftOutput> = {}
): StandardEditDraftOutput {
  return {
    draft: buildStandardDraft(),
    standard_name: "Global Industry Standard on Tailings Management",
    standard_acronym: "GISTM",
    standard_code: "GISTM-2020",
    standard_version: "2020",
    date_issued: "2020-08-05",
    is_active: true,
    hierarchy_levels: [
      {
        client_ref: "L1",
        parent_client_ref: null,
        cod_level: 1,
        name_level_en: "Topic",
        name_level_pt: "Tópico",
        name_level_es: "Tema",
        is_grouping_level: false,
        is_evaluation_level: false,
        is_questioning_level: false,
      },
      {
        client_ref: "L2",
        parent_client_ref: "L1",
        cod_level: 2,
        name_level_en: "Principle",
        name_level_pt: null,
        name_level_es: null,
        is_grouping_level: true,
        is_evaluation_level: false,
        is_questioning_level: false,
      },
      {
        client_ref: "L3",
        parent_client_ref: "L2",
        cod_level: 3,
        name_level_en: "Requirement",
        name_level_pt: null,
        name_level_es: null,
        is_grouping_level: false,
        is_evaluation_level: true,
        is_questioning_level: true,
      },
    ],
    source_file_upload: sourceFileUploadMock,
    structure_upload: structureUploadPassedMock,
    questionnaire_upload: questionnaireUploadPassedMock,
    report_outline_upload: reportOutlineUploadPassedMock,
    report_outline_approved: true,
    ...overrides,
  };
}

export const standardEditDraftMock = buildStandardEditDraft();
